import { Fragment, useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  paymentReconciliationApi,
  type ReconciliationActivityRow,
  type ReconciliationAnalytics,
} from "@/lib/api/payment-reconciliation";
import {
  badgeClass,
  ComparisonPanel,
  formatCurrency,
  PAYMENT_TYPE_OPTIONS,
  PROGRAM_OPTIONS,
  RESULT_OPTIONS,
  SCHOOL_STATUS_OPTIONS,
  SQUARE_STATUS_OPTIONS,
} from "./reconciliation-shared";
import {
  AnalyticsDateRangePicker,
  type AnalyticsDateFilter,
} from "./AnalyticsDateRangePicker";

type MetricKey = "processed" | "due" | "upcoming" | "refunds" | "";

const METRICS: Array<{
  key: MetricKey;
  title: string;
  tone: string;
  icon: string;
  pick: (a: ReconciliationAnalytics) => { amount: number; count: number };
}> = [
  {
    key: "processed",
    title: "Amount Processed",
    tone: "pr-tone-green",
    icon: "✓",
    pick: (a) => a.processed,
  },
  {
    key: "due",
    title: "Amount Due (Overdue)",
    tone: "pr-tone-orange",
    icon: "!",
    pick: (a) => a.due,
  },
  {
    key: "upcoming",
    title: "Upcoming Payments",
    tone: "pr-tone-blue",
    icon: "↗",
    pick: (a) => a.upcoming,
  },
  {
    key: "refunds",
    title: "Refunds",
    tone: "pr-tone-purple",
    icon: "↩",
    pick: (a) => a.refunds,
  },
];

export function ActivityTab() {
  const [rows, setRows] = useState<ReconciliationActivityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [analytics, setAnalytics] = useState<ReconciliationAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeMetric, setActiveMetric] = useState<MetricKey>("");
  const [analyticsDateFilter, setAnalyticsDateFilter] = useState<AnalyticsDateFilter>({
    mode: "month",
  });
  const [program, setProgram] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [schoolStatus, setSchoolStatus] = useState("");
  const [result, setResult] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [schoolStatusFilter, setSchoolStatusFilter] = useState("");
  const [squareStatusFilter, setSquareStatusFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentReconciliationApi.getActivity({
        analyticsDate: analyticsDateFilter.mode,
        start: analyticsDateFilter.mode === "custom" ? analyticsDateFilter.start : undefined,
        end: analyticsDateFilter.mode === "custom" ? analyticsDateFilter.end : undefined,
        program: program || undefined,
        paymentType: paymentType || undefined,
        schoolStatus: schoolStatus || undefined,
        result: result || undefined,
        search: debouncedSearch || undefined,
        schoolStatusFilter: schoolStatusFilter || undefined,
        squareStatusFilter: squareStatusFilter || undefined,
        resultFilter: resultFilter || undefined,
        activeMetric: activeMetric || undefined,
        page,
        limit: 50,
      });
      setRows(res.data.rows);
      setTotal(res.data.total);
      setAnalytics(res.data.analytics);
    } catch {
      toast.error("Failed to load reconciliation activity");
    } finally {
      setLoading(false);
    }
  }, [
    activeMetric,
    analyticsDateFilter,
    page,
    paymentType,
    program,
    result,
    resultFilter,
    schoolStatus,
    schoolStatusFilter,
    debouncedSearch,
    squareStatusFilter,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const clearFilters = () => {
    setActiveMetric("");
    setSearch("");
    setSchoolStatusFilter("");
    setSquareStatusFilter("");
    setResultFilter("");
    setPage(1);
  };

  return (
    <>
      <div className="pr-card">
        <div className="pr-card-head">
          <div>
            <h2>Our System Analytics</h2>
            <p>Filter first, then review school-side payment totals before checking detailed activity.</p>
          </div>
        </div>
        <div className="pr-filter-row">
          <div className="pr-field">
            <label>Analytics Date</label>
            <AnalyticsDateRangePicker
              value={analyticsDateFilter}
              onChange={(next) => {
                setAnalyticsDateFilter(next);
                setPage(1);
              }}
            />
          </div>
          <div className="pr-field">
            <label>Program</label>
            <select value={program} onChange={(e) => setProgram(e.target.value)}>
              <option value="">All Programs</option>
              {PROGRAM_OPTIONS.filter(Boolean).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="pr-field">
            <label>Payment Type</label>
            <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
              <option value="">All Types</option>
              {PAYMENT_TYPE_OPTIONS.filter(Boolean).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="pr-field">
            <label>School Status</label>
            <select value={schoolStatus} onChange={(e) => setSchoolStatus(e.target.value)}>
              <option value="">All Status</option>
              {SCHOOL_STATUS_OPTIONS.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="pr-field">
            <label>Result</label>
            <select value={result} onChange={(e) => setResult(e.target.value)}>
              <option value="">All Results</option>
              {RESULT_OPTIONS.filter(Boolean).map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>
        {analytics && (
          <div className="pr-analytics-cards">
            {METRICS.map((m) => {
              const data = m.pick(analytics);
              return (
                <button
                  key={m.key}
                  type="button"
                  className={`pr-metric ${m.tone} ${activeMetric === m.key ? "active" : ""}`}
                  onClick={() => setActiveMetric(activeMetric === m.key ? "" : m.key)}
                >
                  <div className="pr-metric-top">
                    <div className="pr-metric-title">{m.title}</div>
                    <div className="pr-metric-icon">{m.icon}</div>
                  </div>
                  <div className="pr-metric-number">{formatCurrency(data.amount)}</div>
                  <div className="pr-metric-note">
                    {data.count} school-side record{data.count === 1 ? "" : "s"}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="pr-card">
        <div className="pr-card-head">
          <div>
            <h2>Activity</h2>
            <p>Search activity by name, phone, email, or payment status. Click a row to view school and Square details.</p>
          </div>
        </div>
        <div className="pr-activity-search-row">
          <div className="pr-field">
            <label>Search Activity</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, phone, invoice, payment status..."
            />
          </div>
          <div className="pr-field">
            <label>School Status</label>
            <select value={schoolStatusFilter} onChange={(e) => setSchoolStatusFilter(e.target.value)}>
              <option value="">All School Status</option>
              {SCHOOL_STATUS_OPTIONS.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="pr-field">
            <label>Square Status</label>
            <select value={squareStatusFilter} onChange={(e) => setSquareStatusFilter(e.target.value)}>
              <option value="">All Square Status</option>
              {SQUARE_STATUS_OPTIONS.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="pr-field">
            <label>Reconciliation</label>
            <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
              <option value="">All Results</option>
              {RESULT_OPTIONS.filter(Boolean).map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="pr-pill-btn self-end" onClick={clearFilters}>
            Clear
          </button>
        </div>
        <div className="pr-table-wrap">
          <div className="pr-toolbar">
            <strong>
              {total} Payment Activit{total === 1 ? "y" : "ies"}
            </strong>
            <span style={{ color: "#64748b", fontSize: 13 }}>Accordion format keeps details hidden until needed.</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Invoice</th>
                  <th>Amount</th>
                  <th>School Status</th>
                  <th>Square Status</th>
                  <th>Result</th>
                  <th>Date</th>
                  <th>Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                      No payment activity found
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <Fragment key={row.id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => setOpenId(openId === row.id ? null : row.id)}
                      >
                        <td className="pr-student-cell">
                          <strong>{row.student}</strong>
                          <span>
                            {row.email}
                            <br />
                            {row.phone}
                          </span>
                        </td>
                        <td className="pr-invoice-cell">
                          <strong>{row.invoice}</strong>
                          <span>{row.type}</span>
                        </td>
                        <td>
                          <strong>{formatCurrency(row.amount)}</strong>
                          {row.school === "Overdue" && (row.lateFeeAmount ?? 0) > 0 && (
                            <div
                              style={{
                                color: row.lateFeeApplied ? "#dc2626" : "#b45309",
                                fontSize: 11,
                                marginTop: 4,
                              }}
                            >
                              {row.lateFeeApplied
                                ? `+ ${formatCurrency(row.lateFeeAmount!)} late fee ${
                                    row.lateFeeStatus === "PENDING"
                                      ? "pending"
                                      : "applied"
                                  }`
                                : `+ ${formatCurrency(row.lateFeeAmount!)} late fee due`}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`pr-badge ${badgeClass(row.school)}`}>{row.school}</span>
                        </td>
                        <td>
                          <span className={`pr-badge ${badgeClass(row.square)}`}>{row.square}</span>
                        </td>
                        <td>
                          <span className={`pr-badge ${badgeClass(row.result, row.result)}`}>{row.result}</span>
                        </td>
                        <td>{row.date}</td>
                        <td>
                          <button
                            type="button"
                            className="pr-pill-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenId(openId === row.id ? null : row.id);
                            }}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                      <tr className={`pr-accordion-row ${openId === row.id ? "open" : ""}`}>
                        <td colSpan={8}>
                          <ComparisonPanel row={row} />
                        </td>
                      </tr>
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          )}
          {total > 50 && (
            <div className="flex justify-between items-center mt-4">
              <button
                type="button"
                className="pr-pill-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {Math.ceil(total / 50)}
              </span>
              <button
                type="button"
                className="pr-pill-btn"
                disabled={page >= Math.ceil(total / 50)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
